import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CurrencyInput from '../../ui/inputs/CurrencyInput';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import Select from '../../ui/selects/Select';
import Textarea from '../../ui/inputs/Textarea';
import { ShieldCheck, X } from 'lucide-react';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { useToast } from '../../ui/toast/ToastContext';

const InsuranceModal = ({ clientId, insuranceId = null, scope, onClose, onSuccess }) => {
  const isEdit = !!insuranceId;
  const { addToast } = useToast();

  const [form, setForm] = useState({
    insuranceName: '',
    coverageClass: '',
    insuranceType: '',
    coverageValue: '',
    coveragePremium: '',
    paymentFrequency: '',
    hasBeneficiaries: '',
    isPersonal: false,
    company: '',
    notes: '',
  });

  const [coverageClassOptions, setCoverageClassOptions] = useState([]);
  const [paymentFrequencyOptions, setPaymentFrequencyOptions] = useState([]);

  // Carrega dados do seguro (modo edição)
  useEffect(() => {
    if (isEdit) {
      fetch(`/app/planner/client/${clientId}/dashboard/properties/insurances/${insuranceId}?scope=${scope}`)
        .then((res) => res.json())
        .then((data) => setForm(camelizeKeys(data)))
        .catch((err) => {
          console.error('Erro ao carregar seguro', err);
          addToast({ type: 'error', message: 'Erro ao carregar dados do seguro' });
        });
    }
  }, [isEdit, clientId, insuranceId]);

  // Carrega options do select
  useEffect(() => {
    fetch('/app/shared/form_options')
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);
        setCoverageClassOptions(
          camelData.coverageClasses.map((type) => ({
            label: type.label,
            value: type.value,
          }))
        );
        setPaymentFrequencyOptions(
          camelData.paymentFrequencyTypes.map((type) => ({
            label: type.label,
            value: type.value,
          }))
        );
      })
      .catch((err) => {
        console.error('Erro ao carregar dados do formulário', err);
        addToast({ type: 'error', message: err.message });
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (name) => {
    setForm((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = snakecaseKeys({ insuranceProperty: form });

    const url = isEdit
      ? `/app/planner/client/${clientId}/dashboard/properties/insurances/${insuranceId}?scope=${scope}`
      : `/app/planner/client/${clientId}/dashboard/properties/insurances?scope=${scope}`;

    const method = isEdit ? 'PATCH' : 'POST';
    const successMsg = isEdit ? 'Seguro atualizado com sucesso!' : 'Seguro cadastrado com sucesso!';

    try {
      const res = await fetchWithCsrf(url, {
        method,
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao salvar seguro');
      }

      onClose();
      addToast({ type: 'success', message: successMsg });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <ShieldCheck size={24} className="text-tealMedium" strokeWidth={1.25} />
            {isEdit ? 'Editar Seguro' : 'Cadastrar Seguro'}
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <Input
            name="insuranceName"
            label="Nome da cobertura"
            value={form.insuranceName}
            onChange={handleChange}
            required
          />
          <Select
            name="coverageClass"
            label="Classe do seguro"
            value={form.coverageClass}
            onChange={handleChange}
            options={coverageClassOptions}
          />
          <Input name="insuranceType" label="Tipo" value={form.insuranceType} onChange={handleChange} />
          <Select
            name="paymentFrequency"
            label="Frequência de pagamento"
            value={form.paymentFrequency}
            onChange={handleChange}
            options={paymentFrequencyOptions}
          />
          <CurrencyInput name="coverageValue" label="Cobertura" value={form.coverageValue} onChange={handleChange} />
          <CurrencyInput
            name="coveragePremium"
            label="Prêmio da Cobertura"
            value={form.coveragePremium}
            onChange={handleChange}
          />
          <div className="col-span-2">
            <Input name="company" label="Seguradora" value={form.company} onChange={handleChange} />
          </div>

          <div className="col-span-2">
            <CustomCheckbox
              name="hasBeneficiaries"
              label="Foram nomeados beneficiários durante a contratação"
              checked={form.hasBeneficiaries}
              onChange={() => handleCheckbox('hasBeneficiaries')}
              labelClassName="text-base"
            />
          </div>

          <Textarea
            name="notes"
            label="Observações"
            value={form.notes}
            onChange={handleChange}
            className="md:col-span-2"
            rows={4}
          />

          <div className="md:col-span-2 flex justify-end items-center mt-1">
            <Button type="submit">{isEdit ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR SEGURO'}</Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default InsuranceModal;
